import { useState, useEffect } from 'react';
import { getItems, createItem } from './fitnessApi';
import { latestWeightLbs } from './liftingBurn';
import { walkBurnEstimate } from './walkingBurn';

const WORKOUTS_LIST = 'WalkingWorkouts';
const WEIGHINS_LIST = 'FitnessWeighIns';

// No default selection on purpose: an unselected effort must be visibly
// unselected, not silently 'steady'.
const EFFORTS = [
  { key: 'easy', label: 'Easy' },
  { key: 'steady', label: 'Steady' },
  { key: 'hard', label: 'Hard' },
];

// Picker prefills from LOCAL components, never toISOString().slice — an
// evening entry read in UTC lands on tomorrow, the exact failure the figApi
// date guard documents. Same rule on the way back out: the two picker strings
// are reassembled through the local Date constructor below.
function localDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function localTimeInput(d) {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// 'YYYY-MM-DD' + 'HH:MM' -> local Date, or null when either does not parse.
function dateFromInputs(dateStr, timeStr) {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  const tm = /^(\d{2}):(\d{2})$/.exec(timeStr || '');
  if (!dm || !tm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// A typed value is usable only when it is a finite positive number. A blank
// or unparseable field is ABSENT — it must not become 0 in the payload or in
// the burn preview. Absence is not zero.
function usableNumber(str) {
  if (str === '' || str === null || str === undefined) return null;
  const n = Number(str);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function WalkingLog({ getToken, userEmail }) {
  const [dateStr, setDateStr] = useState(() => localDateInput(new Date()));
  const [timeStr, setTimeStr] = useState(() => localTimeInput(new Date()));
  const [distanceStr, setDistanceStr] = useState('');
  const [minutesStr, setMinutesStr] = useState('');
  const [effort, setEffort] = useState(null);
  const [notes, setNotes] = useState('');
  const [weighIns, setWeighIns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Weigh-ins feed the burn preview only. A failed read leaves the preview a
  // dash and must not block logging a walk — same posture as Food.js, where a
  // fitness list that will not load never takes the primary view down.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const wi = await getItems(token, WEIGHINS_LIST);
        if (!cancelled) setWeighIns(wi.filter(w => w.CreatedByEmail === userEmail));
      } catch (err) {
        console.warn('Weigh-in read failed — burn preview will show a dash:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, userEmail]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  }

  const distanceMiles = usableNumber(distanceStr);
  const durationMinutes = usableNumber(minutesStr);
  const workoutDate = dateFromInputs(dateStr, timeStr);
  const bothMetrics = distanceMiles !== null && durationMinutes !== null;
  const paceMph = bothMetrics ? distanceMiles / (durationMinutes / 60) : null;

  // Live preview — recomputed from form state on every keystroke. null means
  // a dash: no weigh-in on file, no effort picked, or nothing to estimate.
  const weightLbs = latestWeightLbs(weighIns);
  const burn = walkBurnEstimate({
    distanceMiles: distanceMiles === null ? undefined : distanceMiles,
    durationSeconds: durationMinutes === null ? undefined : durationMinutes * 60,
    effort,
    weightLbs,
  });

  // Why the save button is disabled, in one line — or null when it is not.
  const invalidReason =
    effort === null && distanceMiles === null && durationMinutes === null
      ? 'Pick an effort and enter a distance or a duration.'
      : effort === null
        ? 'Pick an effort.'
        : distanceMiles === null && durationMinutes === null
          ? 'Enter a distance or a duration.'
          : workoutDate === null
            ? 'Set a date and time.'
            : null;

  // "2.1 mi · 42m · Aug 7", degrading to whatever is known. The date part is
  // always present, so Title — required by SharePoint — can never be empty.
  function buildTitle() {
    const parts = [];
    if (distanceMiles !== null) parts.push(`${distanceMiles} mi`);
    if (durationMinutes !== null) parts.push(`${Math.round(durationMinutes)}m`);
    parts.push(workoutDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    return parts.join(' · ');
  }

  async function handleSave(e) {
    e.preventDefault();
    if (invalidReason) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      // WorkoutDate comes from the PICKERS, not Date.now() — walks are logged
      // after the fact, and the instant must be the walk's, not the entry's.
      const fields = {
        Title: buildTitle(),
        WorkoutDate: workoutDate.toISOString(),
        Effort: effort,
        CreatedByEmail: userEmail,
      };
      // Absent metrics are OMITTED, never null and never 0 — a key that is
      // not in the payload cannot be misread as a measurement.
      if (distanceMiles !== null) fields.DistanceMiles = distanceMiles;
      if (durationMinutes !== null) fields.DurationSeconds = Math.round(durationMinutes * 60);
      const trimmedNotes = notes.trim();
      if (trimmedNotes) fields.Notes = trimmedNotes;

      await createItem(token, WORKOUTS_LIST, fields);

      const now = new Date();
      setDateStr(localDateInput(now));
      setTimeStr(localTimeInput(now));
      setDistanceStr('');
      setMinutesStr('');
      setEffort(null);
      setNotes('');
      showToast('Saved ✓');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="SwimWorkout">
      {error && <p className="Fitness-error">{error}</p>}
      {toast && <p className="SwimWorkout-saved-toast">{toast}</p>}

      <form onSubmit={handleSave}>
        <div>
          <input
            className="Fitness-input"
            type="date"
            value={dateStr}
            onChange={e => setDateStr(e.target.value)}
          />
          <input
            className="Fitness-input"
            type="time"
            value={timeStr}
            onChange={e => setTimeStr(e.target.value)}
          />
        </div>

        {/* Labels stay visible when the field is filled — miles and minutes
            are the same order of magnitude, and a placeholder that vanishes
            on input let a distance get read as a duration. Same label-wraps-
            input pattern as SwimmingWorkout's edit overlay. */}
        <div>
          <label>
            Distance (mi)
            <input
              className="Fitness-input"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={distanceStr}
              onChange={e => setDistanceStr(e.target.value)}
            />
          </label>
          <label>
            Duration (min)
            <input
              className="Fitness-input"
              type="number"
              step="1"
              min="0"
              inputMode="numeric"
              value={minutesStr}
              onChange={e => setMinutesStr(e.target.value)}
            />
          </label>
        </div>

        <div>
          {EFFORTS.map(ef => (
            <button
              key={ef.key}
              type="button"
              className={`Fitness-btn${effort === ef.key ? ' Fitness-btn--active' : ' Fitness-btn--secondary'}`}
              onClick={() => setEffort(ef.key)}
            >
              {ef.label}
            </button>
          ))}
        </div>
        {bothMetrics && (
          <p className="Fitness-empty">
            {paceMph.toFixed(1)} mph — how hard, not how fast — pace is already measured
          </p>
        )}

        <textarea
          className="Fitness-input"
          rows={3}
          placeholder="Notes (optional)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />

        {/* Burn preview. A dash is a real answer: no weigh-in, no effort, or
            nothing to estimate. Never 0, never a placeholder number. The
            weight in force is shown alongside — an estimate that scales
            invisibly with a hidden input is how a wrong weight goes
            unnoticed. */}
        <p>
          {burn === null && <span title="Needs a weigh-in, an effort, and a distance or duration">&mdash;</span>}
          {burn !== null && burn.basis === 'measured' && `~${burn.mid.toLocaleString('en-US')} cal`}
          {burn !== null && burn.basis === 'assumed' && `~${burn.low.toLocaleString('en-US')}–${burn.high.toLocaleString('en-US')} cal`}
          {burn !== null && burn.suspect && paceMph !== null && (
            <span className="Fitness-error"> · {paceMph.toFixed(1)} mph — check your numbers</span>
          )}
        </p>
        {burn !== null && (
          <p className="Fitness-empty">at {Math.round(weightLbs)} lb</p>
        )}
        {weightLbs === null && (
          <p className="Fitness-empty">no weigh-in on file — log one under Swimming or Lifting to see an estimate</p>
        )}

        {invalidReason && <p className="Fitness-empty">{invalidReason}</p>}
        <button className="Fitness-btn" type="submit" disabled={saving || invalidReason !== null}>
          {saving ? 'Saving...' : 'Save Walk'}
        </button>
      </form>
    </div>
  );
}

export default WalkingLog;
